import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DatePipe } from '@angular/common';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { AppState } from 'src/app/reducers';
import { ExportSchedule } from 'src/app/core/models/core.models';
import { toggleExportScheduleStatus } from 'src/app/core/actions/object.actions';

interface ScheduleInfo {
  icon: IconProp;
  label: string;
  value: any;
}

@Component({
  selector: 'app-schedule-card',
  templateUrl: './schedule-card.component.html',
  styleUrl: './schedule-card.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ScheduleCardComponent {
  @Input() schedule: ExportSchedule;
  exportScheduleProperties: ScheduleInfo[];
  @Output() editSchedule = new EventEmitter<ExportSchedule>();
  @Output() deleteSchedule = new EventEmitter<ExportSchedule>();

  constructor(
    private datePipe: DatePipe,
    private translateService: TranslateService,
    private store: Store<AppState>,
  ) {}

  ngOnInit(): void {
    // console.log('schedule', this.schedule);
    const emails = this.schedule.emails?.join(', ') ?? '';
    const channels = this.schedule.selected_channels_names?.join(', ') ?? '';

    let recipients = '';
    if (emails) {
      recipients = emails + ', ' + channels;
    } else {
      recipients = channels;
    }

    const weekdays = this.schedule.list_weekdays?.join(', ') ?? '';
    const languages = (this.schedule.languages ?? [])
      .filter((language) => language.selected)
      .map((language) => language.name)
      .join(', ');

    this.exportScheduleProperties = [
      {
        icon: ['fas', 'envelope'],
        label: 'schedule-card.label.subject',
        value: this.schedule?.subject,
      },
      {
        icon: ['fas', 'calendar'],
        label: 'schedule-card.label.start-date',
        value: this.datePipe.transform(
          this.schedule.schedule_start_date * 1000,
          'dd.MM.yyyy, HH:mm',
        ),
      },
      {
        icon: ['fas', 'calendar'],
        label: 'schedule-card.label.end-date',
        value: this.schedule.schedule_end_date
          ? this.datePipe.transform(
              this.schedule.schedule_end_date * 1000,
              'dd.MM.yyyy, HH:mm',
            )
          : this.translateService.instant('schedule-card.label.no-end-date'),
      },
      {
        icon: ['fas', 'calendar'],
        label: 'schedule-card.label.last-sent-time',
        value: this.schedule.time_last_sent?.absolute
          ? this.datePipe.transform(
              this.schedule.time_last_sent.timestamp * 1000,
              'dd.MM.yyyy, HH:mm',
            )
          : this.translateService.instant('schedule-card.label.never'),
      },
      {
        icon: ['fas', 'calendar'],
        label: 'schedule-card.label.next-scheduled-time',
        value: this.schedule.time_next_trigger?.absolute
          ? this.datePipe.transform(
              this.schedule.time_next_trigger.timestamp * 1000,
              'dd.MM.yyyy, HH:mm',
            )
          : this.translateService.instant('schedule-card.label.never'),
      },

      {
        icon: ['fas', 'calendar'],
        label: 'schedule-card.label.coverage-period',
        value: this.schedule.list_cover_period,
      },

      {
        icon: ['fas', 'calendar'],
        label: 'schedule-card.label.coverage-perion-end-time',
        value: this.schedule.schedule_cover_period_end_time,
      },
      {
        icon: ['fas', 'calendar'],
        label: 'schedule-card.label.time-of-day-to-send',
        value: this.schedule.schedule_time_of_day_to_send,
      },

      {
        icon: ['fas', 'calendar'],
        label: 'schedule-card.label.send-on',
        value: weekdays,
      },

      {
        icon: ['fas', 'share'],
        label: 'schedule-card.label.recipients-count',
        value: this.schedule.recipients_count,
      },

      {
        icon: ['fas', 'share'],
        label: 'schedule-card.label.recipients',
        value: recipients,
      },

      {
        icon: ['fas', 'earth'],
        label: 'schedule-card.label.output-language',
        value: languages,
      },
    ];
  }

  edit(schedule: ExportSchedule) {
    this.editSchedule.emit(schedule);
  }

  toggleScheduleStatus(exportScheduleId) {
    this.store.dispatch(toggleExportScheduleStatus({ exportScheduleId }));
  }

  delete(schedule: ExportSchedule) {
    this.deleteSchedule.emit(schedule);
  }
}
